// src/stores/AuthStore.ts
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from './index';
import authService from '@/services/authService';
import groupService from '@/services/groupService';
import roleService from '@/services/roleService';

export interface RegisterFormData {
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    organization?: string;
    taxId?: string;
    groupCode: string;
    groupCodes?: string[]; // Thêm mới để hỗ trợ nhiều nhóm
    roleCodes: string[];
    agreeTerms: boolean;
}

export interface User {
    id: number;
    documentId: string;
    username: string;
    email: string;
    provider: string;
    confirmed: boolean;
    blocked: boolean;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    locale: string | null;
    user_profile: any | null;
    groups: {
        id: number;
        documentId: string;
        name: string;
        code: string;
        description: string;
        createdAt: string;
        updatedAt: string;
        publishedAt: string;
        locale: string | null;
        registrationType: string;
        isActive: boolean;
        registrationMessage: string | null;
        order: number | null;
    }[];
    role: {
        id: number;
        documentId: string;
        name: string;
        description: string;
        type: string;
        createdAt: string;
        updatedAt: string;
        publishedAt: string;
        locale: string | null;
    };
    roleFunctionCodes?: string[];
    firstName?: string;
    lastName?: string;
    fullName?: string;
    avatar?: string;
    displayName?: string;
}

class AuthStore {
    rootStore: RootStore;
    currentUser: User | null = null;
    isAuthenticated: boolean = false;
    isLoading: boolean = false;
    error: string | null = null;
    groups: any[] = [];
    roles: any[] = [];
    registerFormData: RegisterFormData = {
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        organization: '',
        taxId: '',
        groupCode: '',
        groupCodes: [], // Thêm mới
        roleCodes: [],
        agreeTerms: false
    };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, { rootStore: false });

        // Kiểm tra token trong localStorage khi khởi tạo
        this.checkAuthStatus();
    }

    async checkAuthStatus() {
        if (authService.isAuthenticated()) {
            this.isLoading = true;
            try {
                // Lấy thông tin user với profile và groups
                const userData = await authService.getUserWithProfileAndGroups();

                runInAction(() => {
                    // Tạo fullName từ firstName và lastName nếu có
                    const fullName = userData.firstName && userData.lastName
                        ? `${userData.firstName} ${userData.lastName}`
                        : userData.username;

                    this.currentUser = {
                        ...userData,
                        fullName
                    };
                    this.isAuthenticated = true;
                    this.isLoading = false;

                    localStorage.setItem('userRole', this.userRole);
                    localStorage.setItem('userId', this.currentUser?.id?.toString());
                });
            } catch (error) {
                runInAction(() => {
                    this.currentUser = null;
                    this.isAuthenticated = false;
                    this.isLoading = false;
                    authService.logout();
                });
            }
        }
    }

    async fetchGroups() {
        this.isLoading = true;
        try {
            // Sử dụng service
            const groups = await groupService.getRegisterableGroups();

            runInAction(() => {
                this.groups = groups;
                this.isLoading = false;
            });
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.isLoading = false;
            });
        }
    }

    async fetchRoles() {
        this.isLoading = true;
        try {
            const roles = await roleService.getAllRoles();

            runInAction(() => {
                this.roles = roles;
                this.isLoading = false;
            });
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.isLoading = false;
            });
        }
    }

    setRegisterFormDataOld(field, value) {
        this.registerFormData = {
            ...this.registerFormData,
            [field]: value
        };

        // Nếu thay đổi groupCode, cập nhật roleCodes dựa trên group được chọn
        if (field === 'groupCode') {
            const selectedGroup = this.groups.find(g => g.code === value);
            if (selectedGroup && selectedGroup.roleFunctionCodes) {
                this.registerFormData.roleCodes = selectedGroup.roleFunctionCodes;
            } else {
                this.registerFormData.roleCodes = [];
            }
        }
    }

    setRegisterFormData<K extends keyof RegisterFormData>(
        field: K,
        value: RegisterFormData[K]
    ) {
        this.registerFormData[field] = value;
        
        if (field === "groupCode") {
            const selectedGroup = this.groups.find(g => g.code === value);
            if (selectedGroup && selectedGroup.roleFunctionCodes) {
            this.registerFormData.roleCodes = selectedGroup.roleFunctionCodes;
            } else {
            this.registerFormData.roleCodes = [];
            }
        }
    }

    async register() {
        this.isLoading = true;
        this.error = null;

        try {
            // Username được nhập trực tiếp bởi người dùng, không cần tạo tự động nữa
            // Chúng ta vẫn giữ đoạn code này để đảm bảo luôn có username
            if (!this.registerFormData.username) {
                this.registerFormData.username = this.registerFormData.email.split('@')[0] + Math.floor(Math.random() * 1000);
            }

            // Sử dụng service
            const response = await authService.register(this.registerFormData);

            runInAction(() => {
                // Tạo fullName từ firstName và lastName
                const fullName = response.user.firstName && response.user.lastName
                    ? `${response.user.firstName} ${response.user.lastName}`
                    : response.user.username;

                this.currentUser = {
                    ...response.user,
                    fullName
                };
                this.isAuthenticated = !response.needsApproval;
                this.isLoading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Đăng ký thất bại';
                this.isLoading = false;
            });
            throw error;
        }
    }

    async login(identifier: string, password: string) {
        this.isLoading = true;
        this.error = null;

        try {
            const response = await authService.login({ identifier, password });

            runInAction(() => {
                // Tạo fullName từ firstName và lastName nếu có
                const fullName = response.user.firstName && response.user.lastName
                    ? `${response.user.firstName} ${response.user.lastName}`
                    : response.user.username;

                this.currentUser = {
                    ...response.user,
                    fullName
                };
                this.isAuthenticated = true;
                this.isLoading = false;
                localStorage.setItem('userRole', this.userRole);
                localStorage.setItem('userId', this.currentUser.id.toString());
            });

            return response;
        } catch (error:any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.name || error.response?.data?.error?.message || 'Đăng nhập thất bại';
                this.isLoading = false;
            });
            throw error;
        }
    }

    logout() {
        authService.logout();
        runInAction(() => {
            this.currentUser = null;
            this.isAuthenticated = false;

            // ✅ XÓA USERROLE KHỎI LOCALSTORAGE
            localStorage.removeItem('userRole');
            localStorage.removeItem('userId');
        })
    }

    resetRegisterForm() {
        this.registerFormData = {
            username: '',
            email: '',
            password: '',
            firstName: '',
            lastName: '',
            phone: '',
            organization: '',
            taxId: '',
            groupCode: '',
            groupCodes: [],
            roleCodes: [],
            agreeTerms: false
        };
    }

    // 🔧 FIX: Lấy role hiện tại của người dùng (fixed logic)
    get userRole() {
        if (!this.currentUser) return 'guest';

        // ✅ FIX: Sử dụng determineRoleFromGroup để xác định role từ group thay vì hardcode
        if (this.currentUser.role && this.currentUser.role.type) {
            // Map từ role type của Strapi sang role trong ứng dụng
            const roleMap = {
                'authenticated': this.determineRoleFromGroup(), // ✅ FIX: Gọi method xác định từ group
                'public': 'guest',
                'admin': 'admin'
            };
            return roleMap[this.currentUser.role.type] || 'guest';
        }

        // Ngược lại, duy trì logic cũ sử dụng roleCodes/roleFunctionCodes
        const roleFunctionCodes = this.currentUser.roleFunctionCodes || [];

        // Ưu tiên role cao nhất
        const rolePriority = ['admin', 'legal', 'monitor', 'right_owner', 'licensee', 'guest'];

        for (const role of rolePriority) {
            if (roleFunctionCodes.includes(role)) {
                return role;
            }
        }

        return this.determineRoleFromGroup();
    }

    // ✅ FIX: Method xác định role từ group
    private determineRoleFromGroup(): string {
        if (!this.currentUser || !this.currentUser.groups) return 'guest';

        const groupCodes = this.currentUser.groups.map(group => group.code);

        // Xác định role dựa trên group code
        if (groupCodes.includes('ADMIN_USERS')) return 'admin';
        if (groupCodes.includes('PRIMARY_USERS')) return 'right_owner';
        if (groupCodes.includes('MONITORING_TEAM')) return 'monitor';
        if (groupCodes.includes('LEGAL_TEAM')) return 'legal';

        // ✅ FIX: SUPPORT_MONITORING group sẽ có role là support_specialist
        // Nhưng nếu là legal user trong SUPPORT_MONITORING thì vẫn là legal
        if (groupCodes.includes('SUPPORT_MONITORING')) {
            // Kiểm tra xem user có phải legal không qua email/username pattern
            const email = this.currentUser.email?.toLowerCase() || '';
            const username = this.currentUser.username?.toLowerCase() || '';

            if (email.includes('legal') || username.includes('legal')) {
                return 'legal';
            }
            return 'support_specialist';
        }

        if (groupCodes.includes('EDUCATION_USERS')) return 'teacher'; // Default education role
        if (groupCodes.includes('BUSINESS_DEVELOPMENT')) return 'business_developer';
        if (groupCodes.includes('RISK_SECURITY')) return 'security_analyst';

        return 'guest';
    }

    // ✅ FIX: Method để lấy tất cả roles của user
    get userRoles(): string[] {
        const primaryRole = this.userRole;
        const additionalRoles: string[] = [];

        if (!this.currentUser || !this.currentUser.groups) return [primaryRole];

        const groupCodes = this.currentUser.groups.map(group => group.code);

        // Thêm các roles khác dựa trên group
        if (groupCodes.includes('SUPPORT_MONITORING')) {
            additionalRoles.push('support_specialist');
            // Nếu là legal user, cũng thêm legal role
            const email = this.currentUser.email?.toLowerCase() || '';
            const username = this.currentUser.username?.toLowerCase() || '';
            if (email.includes('legal') || username.includes('legal')) {
                additionalRoles.push('legal');
            }
        }

        // Thêm các roles bổ sung khác nếu cần
        if (groupCodes.includes('LEGAL_TEAM')) {
            additionalRoles.push('legal');
        }

        if (groupCodes.includes('ADMIN_USERS')) {
            additionalRoles.push('admin', 'staff'); // Admin cũng có quyền staff
        }

        // Loại bỏ trùng lặp và trả về
        const allRoles = [primaryRole, ...additionalRoles];
        return [...new Set(allRoles)];
    }

    // Lấy mã nhóm của người dùng
    get userGroupCodes() {
        if (!this.currentUser || !this.currentUser.groups) return [];

        return this.currentUser.groups.map(group => group.code);
    }

    // Lấy mã role function của người dùng
    get userRoleFunctionCodes() {
        if (!this.currentUser) return [];

        return this.currentUser.roleFunctionCodes || [];
    }

    // ✅ FIX: Debug method để kiểm tra user roles và quyền truy cập
    debugUserAccess() {
        if (!this.currentUser) {
            console.log('🚫 No current user');
            return;
        }

        console.group('🔍 Debug User Access');
        console.log('Current User:', this.currentUser);
        console.log('User Groups:', this.userGroupCodes);
        console.log('Primary Role:', this.userRole);
        console.log('All Roles:', this.userRoles);

        // Kiểm tra access to certificate approval
        const approvalRoute = {
            roles: ['admin', 'staff', 'legal', 'support_specialist']
        };

        const hasApprovalAccess = approvalRoute.roles.some(role =>
            this.userRoles.includes(role)
        );

        console.log('Can access certificate approval:', hasApprovalAccess);
        console.log('Required roles for approval:', approvalRoute.roles);
        console.log('User email contains "legal":', this.currentUser.email?.toLowerCase().includes('legal'));
        console.log('User username contains "legal":', this.currentUser.username?.toLowerCase().includes('legal'));
        console.groupEnd();
    }
}

export default AuthStore;
